import React, { useEffect, useState } from "react";
import "./NLHArticle.css";
import GuideHeader from '../components/GuideHeader';

type Suit = "s" | "h" | "d" | "c";

type PokerCardProps = {
  rank: string;
  suit: Suit;
  selected?: boolean;
  muted?: boolean;
  compact?: boolean;
};

const SUITS: Record<Suit, { symbol: string; label: string; red: boolean }> = {
  s: { symbol: "♠", label: "スペード", red: false },
  h: { symbol: "♥", label: "ハート", red: true },
  d: { symbol: "♦", label: "ダイヤ", red: true },
  c: { symbol: "♣", label: "クラブ", red: false },
};

function PokerCard({ rank, suit, selected = false, muted = false, compact = false }: PokerCardProps) {
  const meta = SUITS[suit];
  return (
    <span
      className={`p-card ${meta.red ? "is-red" : ""} ${selected ? "is-selected" : ""} ${muted ? "is-muted" : ""} ${compact ? "is-compact" : ""}`}
      aria-label={`${rank}${meta.label}`}
    >
      <span className="p-card__rank">{rank}</span>
      <span className="p-card__suit">{meta.symbol}</span>
    </span>
  );
}

function CardRow({ children, label }: { children: React.ReactNode; label?: string }) {
  return (
    <div className="card-row-wrap">
      {label && <div className="card-row-label">{label}</div>}
      <div className="card-row">{children}</div>
    </div>
  );
}

const sections = [
  ["intro", "NLHとは"],
  ["setup", "6人テーブルのポジション"],
  ["actions", "自分の番でできること"],
  ["flow", "1ハンドの流れ"],
  ["ranking", "役の強さ"],
  ["best-five", "ベスト5枚の作り方"],
  ["nolimit", "ノーリミット"],
  ["starting", "最初の手札の見方"],
];

const handRankings = [
  { no: "1", name: "ロイヤルフラッシュ", note: "同じスートの A-K-Q-J-10", cards: [["A","s"],["K","s"],["Q","s"],["J","s"],["10","s"]] as [string,Suit][] },
  { no: "2", name: "ストレートフラッシュ", note: "同じスートで5枚連続", cards: [["9","d"],["8","d"],["7","d"],["6","d"],["5","d"]] as [string,Suit][] },
  { no: "3", name: "フォーカード（フォーオブアカインド / クワッズ）", note: "同じ数字が4枚", cards: [["8","s"],["8","h"],["8","d"],["8","c"],["K","s"]] as [string,Suit][] },
  { no: "4", name: "フルハウス", note: "スリーカード＋ワンペア", cards: [["Q","s"],["Q","h"],["Q","d"],["7","c"],["7","s"]] as [string,Suit][] },
  { no: "5", name: "フラッシュ", note: "同じスートが5枚", cards: [["A","h"],["J","h"],["8","h"],["5","h"],["2","h"]] as [string,Suit][] },
  { no: "6", name: "ストレート", note: "スートを問わず5枚連続", cards: [["10","s"],["9","h"],["8","d"],["7","c"],["6","s"]] as [string,Suit][] },
  { no: "7", name: "スリーカード（スリーオブアカインド）", note: "同じ数字が3枚", cards: [["J","s"],["J","h"],["J","d"],["A","c"],["7","s"]] as [string,Suit][] },
  { no: "8", name: "ツーペア", note: "2組のワンペア", cards: [["K","s"],["K","d"],["9","h"],["9","c"],["A","s"]] as [string,Suit][] },
  { no: "9", name: "ワンペア", note: "同じ数字が2枚", cards: [["A","s"],["A","h"],["10","d"],["7","c"],["4","s"]] as [string,Suit][] },
  { no: "10", name: "ハイカード", note: "上の役がない場合", cards: [["A","s"],["J","d"],["9","c"],["6","h"],["3","s"]] as [string,Suit][] },
];

export default function NLHArticle() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  return (
    <div className="nlh-page">
      <GuideHeader />

      <main>
        <section className="article-hero">
          <div className="article-hero__inner">
            <div className="hero-copy">
              <nav className="breadcrumb" aria-label="パンくず"><a href="/">トップ</a><span>›</span><a href="/rules">ポーカー各種ルール</a><span>›</span><span>NLH</span></nav>
              <p className="eyebrow">POKER RULES / NLH</p>
              <h1>NLH（ノーリミット・<br className="desktop-only" />テキサスホールデム）のルール</h1>
              <p className="hero-lead">完全未経験でも、このページを上から読めばそのまま1ゲーム始められるように、<strong>ポジション・アクション・ゲーム進行・役の強さ</strong>を必要な順番でまとめました。</p>
            </div>
            <div className="hero-hand" aria-label="ポケットエースの例 Aスペード Aハート">
              <div className="hero-hand__caption"><span>STARTING HAND</span> ポケットエース</div>
              <div className="hero-hand__cards hero-hand__cards--two">
                <PokerCard rank="A" suit="s" />
                <PokerCard rank="A" suit="h" />
              </div>
              <p><b>同じ数字の2枚を「ポケットペア」と呼びます。</b><br />AAはNLHで最も強いスターティングハンドです。</p>
            </div>
          </div>
        </section>

        <div className="article-shell">
          <aside className="toc" aria-label="目次">
            <div className="toc__title">CONTENTS</div>
            <ol>{sections.map(([id, label], i) => <li key={id}><a href={`#${id}`}><span>{String(i + 1).padStart(2, "0")}</span>{label}</a></li>)}</ol>
            <div className="toc__note">このページだけで<br />基本ルールを確認しながら遊べます。</div>
          </aside>

          <article className="article-body">
            <section id="intro" className="section-block">
              <div className="section-head"><span>01</span><div><p>ABOUT</p><h2>NLHとは？</h2></div></div>
              <p className="section-intro">NLH（No Limit Texas Hold'em）は、各プレイヤーに<strong>2枚の手札</strong>が配られ、テーブル中央に最大<strong>5枚の共通カード（ボード）</strong>が出るポーカーです。最後まで残ったプレイヤーは、合計7枚の中から最も強い5枚の組み合わせを作ります。</p>
              <div className="formula-strip">
                <div><small>YOUR HAND</small><b>手札 2枚</b><CardRow><PokerCard rank="A" suit="s" compact /><PokerCard rank="K" suit="d" compact /></CardRow></div>
                <div className="formula-strip__plus">＋</div>
                <div><small>COMMUNITY CARDS</small><b>ボード 最大5枚</b><CardRow><PokerCard rank="A" suit="h" compact /><PokerCard rank="7" suit="c" compact /><PokerCard rank="7" suit="d" compact /><PokerCard rank="4" suit="s" compact /><PokerCard rank="2" suit="c" compact /></CardRow></div>
              </div>
              <div className="win-note"><b>勝ち方は2つ。</b><span>① 他の全員をフォールドさせる　② ショーダウンで最も強い5枚の役を作る</span></div>
            </section>

            <section id="setup" className="section-block">
              <div className="section-head"><span>02</span><div><p>POSITIONS</p><h2>6人テーブルのポジション</h2></div></div>
              <p className="section-intro">6人で遊ぶ場合は、まずテーブル上の位置関係を把握すると流れが分かりやすくなります。基本ポジションは<strong>UTG・HJ・CO・BTN・SB・BB</strong>の6つです。</p>
              <div className="position-layout">
                <div className="position-diagram">
                  <div className="position-diagram__table"></div>
                  <div className="position-seat position-seat--utg"><b>UTG</b><span>BBの左側</span></div>
                  <div className="position-seat position-seat--hj"><b>HJ</b><span>UTGの次</span></div>
                  <div className="position-seat position-seat--co"><b>CO</b><span>ボタンの右側</span></div>
                  <div className="position-seat position-seat--btn"><b>BTN</b><span>ボタン</span></div>
                  <div className="position-seat position-seat--sb"><b>SB</b><span>ボタンの左側</span></div>
                  <div className="position-seat position-seat--bb"><b>BB</b><span>SBの左側</span></div>
                  <div className="position-chip position-chip--sb">100</div>
                  <div className="position-chip position-chip--bb">200</div>
                  <div className="position-diagram__rotation">ハンドごとに各ポジションが時計回りに1つずつ回る</div>
                </div>
                <div className="position-summary">
                  <h3>最初に覚えるポイント</h3>
                  <ul>
                    <li><strong>プリフロップ</strong>は、<strong>BBの左側（UTG）</strong>からアクション開始。</li>
                    <li><strong>フロップ以降</strong>は、<strong>ボタンの左側（SB）</strong>からアクション開始。</li>
                    <li>BTN（ボタン）は基準になる位置で、<strong>ハンドが終わるごとに時計回りに移動</strong>します。</li>
                    <li>SBとBBは、カードが配られる前にあらかじめ出す強制ベットです。</li>
                  </ul>
                </div>
              </div>
              <div className="blind-strip">
                <div className="blind-card blind-card--btn"><span>BTN</span><b>ボタン</b><p>基準になる位置。1ハンドごとに時計回りに1席移動。</p></div>
                <div className="blind-arrow">→</div>
                <div className="blind-card"><span>SB</span><b>スモールブラインド</b><p>ボタンの左隣。例：100を強制的に出す。</p></div>
                <div className="blind-arrow">→</div>
                <div className="blind-card"><span>BB</span><b>ビッグブラインド</b><p>SBの左隣。例：200を強制的に出す。</p></div>
              </div>
              <div className="deal-order-note"><b>手札の配り方</b><p>ボタンの左隣から時計回りに1枚ずつ配り、全員に2周して2枚ずつ。手札は裏向きで配ります。</p></div>
              <p className="small-note">友人同士で始めるなら、まずは「SB 100 / BB 200」のようにブラインド額を決めればOKです。2人だけで遊ぶ場合は、ボタンがSBを兼ねます。</p>
            </section>

            <section id="actions" className="section-block">
              <div className="section-head"><span>03</span><div><p>ACTIONS</p><h2>自分の番でできること</h2></div></div>
              <p className="section-intro">自分まで順番が回ってきたら、状況に応じて次のアクションを選びます。特に<strong>「まだ誰もベットしていないか / すでに誰かがベットしているか」</strong>で選べる内容が変わります。</p>
              <div className="action-groups">
                <div className="action-group">
                  <div className="action-group__head"><span>CASE 1</span><h3>誰もベットしていない場合</h3><p className="action-group__sub">選べるのは <strong>（フォールド）・チェック・ベット</strong>。オールインもできます。</p></div>
                  <div className="action-badges">
                    <div className="action-badge"><b>フォールド</b><p>降りてこのハンドを終えます。チップを出さずにやめたいとき。</p></div>
                    <div className="action-badge"><b>チェック</b><p>追加でチップを出さず、次の人に回します。</p></div>
                    <div className="action-badge"><b>ベット</b><p>自分から賭けを始めます。<em>残り全額ならオールイン</em></p></div>
                  </div>
                </div>
                <div className="action-group">
                  <div className="action-group__head"><span>CASE 2</span><h3>誰かがベットしている場合</h3><p className="action-group__sub">選べるのは <strong>フォールド・コール・レイズ</strong>。レイズ額をすべて出せばオールインです。</p></div>
                  <div className="action-badges">
                    <div className="action-badge"><b>フォールド</b><p>相手のベットに付き合わず、このハンドを降ります。</p></div>
                    <div className="action-badge"><b>コール</b><p>現在のベット額と同じところまでチップを出して参加を続けます。</p></div>
                    <div className="action-badge"><b>レイズ</b><p>現在のベット額をさらに引き上げます。<em>残り全額ならオールイン</em></p></div>
                  </div>
                </div>
              </div>
              <div className="round-end-note"><b>次のカードへ進むタイミング</b><p>フォールドしていない全員の出している額がそろい、これ以上レイズする人がいなくなったら、そのベッティングラウンドは終了です。</p></div>
            </section>

            <section id="flow" className="section-block">
              <div className="section-head"><span>04</span><div><p>GAME FLOW</p><h2>1ハンドの流れ</h2></div></div>
              <p className="section-intro">カードが増えるたびにベッティングラウンドがあります。途中で自分以外の全員がフォールドした場合、その時点でハンド終了し、ポットにあるチップを獲得できます。</p>
              <div className="flow-line flow-line--five">
                <div className="flow-step"><i>1</i><b>プリフロップ</b><CardRow><PokerCard rank="?" suit="s" compact muted /><PokerCard rank="?" suit="s" compact muted /></CardRow><p>全員に手札2枚。<strong>BBの左側（UTG）からアクション</strong>。</p></div>
                <div className="flow-step"><i>2</i><b>フロップ</b><CardRow><PokerCard rank="Q" suit="s" compact /><PokerCard rank="8" suit="h" compact /><PokerCard rank="3" suit="d" compact /></CardRow><p>3枚公開。<strong>ボタンの左側（SB）からアクション</strong>。</p></div>
                <div className="flow-step"><i>3</i><b>ターン</b><CardRow><PokerCard rank="Q" suit="s" compact /><PokerCard rank="8" suit="h" compact /><PokerCard rank="3" suit="d" compact /><PokerCard rank="K" suit="c" compact /></CardRow><p>4枚目を公開してベッティング。</p></div>
                <div className="flow-step"><i>4</i><b>リバー</b><CardRow><PokerCard rank="Q" suit="s" compact /><PokerCard rank="8" suit="h" compact /><PokerCard rank="3" suit="d" compact /><PokerCard rank="K" suit="c" compact /><PokerCard rank="2" suit="h" compact /></CardRow><p>5枚目を公開して最後のベッティング。</p></div>
                <div className="flow-step flow-step--showdown"><i>5</i><b>ショーダウン</b><div className="showdown-mark">5</div><p>2人以上残っていれば、最も強い5枚の役が勝ち。</p></div>
              </div>
              <div className="showdown-example">
                <div className="showdown-example__head"><span>SHOWDOWN EXAMPLE</span><h3>⑤ ショーダウンの例</h3></div>
                <div className="showdown-board"><CardRow label="ボード"><PokerCard rank="A" suit="s" compact /><PokerCard rank="K" suit="h" compact /><PokerCard rank="Q" suit="d" compact /><PokerCard rank="7" suit="c" compact /><PokerCard rank="7" suit="s" compact /></CardRow></div>
                <div className="showdown-grid">
                  <div className="showdown-player"><div className="showdown-player__top"><b>プレイヤーA</b><span className="showdown-badge showdown-badge--win">WIN</span></div><CardRow label="手札"><PokerCard rank="A" suit="d" compact /><PokerCard rank="7" suit="h" compact /></CardRow><p className="showdown-player__result"><strong>フルハウス</strong><br />使う5枚：A♠ A♦ 7♣ 7♠ 7♥</p></div>
                  <div className="showdown-player"><div className="showdown-player__top"><b>プレイヤーB</b><span className="showdown-badge showdown-badge--lose">LOSE</span></div><CardRow label="手札"><PokerCard rank="J" suit="s" compact /><PokerCard rank="10" suit="s" compact /></CardRow><p className="showdown-player__result"><strong>ストレート</strong><br />使う5枚：A♠ K♥ Q♦ J♠ 10♠</p></div>
                </div>
                <p className="small-note">この場合は、<strong>フルハウスがストレートに勝ちます。</strong></p>
              </div>
              <div className="deal-note"><span>実際にカードを配る場合</span><p>フロップ・ターン・リバーを出す直前に、それぞれ山札の一番上を1枚伏せて捨てます。これを<strong>バーンカード</strong>と呼びます。</p></div>
            </section>

            <section id="ranking" className="section-block">
              <div className="section-head"><span>05</span><div><p>HAND RANKINGS</p><h2>役の強さ</h2></div></div>
              <p className="section-intro">上にある役ほど強くなります。最初のうちは、この一覧を見ながらプレイして問題ありません。</p>
              <div className="ranking-layout">
                <div className="ranking-axis"><span>強</span><span className="ranking-axis__arrow">↑ ↓</span><span>弱</span></div>
                <div className="ranking-list">
                  {handRankings.map((hand) => (
                    <div className="ranking-row" key={hand.no}>
                      <span className="ranking-no">{hand.no}</span>
                      <div className="ranking-name"><b>{hand.name}</b><small>{hand.note}</small></div>
                      <CardRow>{hand.cards.map(([rank,suit], idx) => <PokerCard key={`${hand.no}-${idx}`} rank={rank} suit={suit} compact />)}</CardRow>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rank-strength"><b>数字の強さ</b><p>A → K → Q → J → 10 → 9 → 8 → 7 → 6 → 5 → 4 → 3 → 2 の順に強くなります。</p></div>
              <div className="ranking-notes"><p><b>ストレートのつながり方</b> A-2-3-4-5 はつながりますが、J-Q-K-A-2 はつながりません（A2345 ＜ 23456）。</p><p><b>スートに強弱はありません。</b> ♠が♥より強い、ということはありません。</p></div>
            </section>

            <section id="best-five" className="section-block important-section">
              <div className="section-head"><span>06</span><div><p>MOST IMPORTANT</p><h2>7枚の中から「一番強い5枚」を使う</h2></div></div>
              <p className="section-intro">NLHでは、手札2枚とボード5枚の合計7枚から<strong>ベスト5枚</strong>を選びます。手札を必ず2枚使う必要はありません。</p>
              <div className="best-five-demo">
                <div><CardRow label="あなたの手札"><PokerCard rank="A" suit="s" selected /><PokerCard rank="K" suit="d" selected /></CardRow></div>
                <div className="plus">＋</div>
                <div><CardRow label="ボード"><PokerCard rank="A" suit="h" selected /><PokerCard rank="7" suit="c" selected /><PokerCard rank="7" suit="d" selected /><PokerCard rank="4" suit="s" muted /><PokerCard rank="2" suit="c" muted /></CardRow></div>
              </div>
              <div className="result-line"><span>完成する役</span><b>Aと7のツーペア（Kキッカー）</b><em>使う5枚：A♠ A♥ 7♣ 7♦ K♦</em></div>
              <div className="point-list compact-points">
                <div className="point-row"><span className="point-index">①</span><div><h3>手札は0枚・1枚・2枚、どれでもよい</h3><p>ボード5枚だけが最強なら、手札を1枚も使わないこともあります。</p></div></div>
                <div className="point-row"><span className="point-index">②</span><div><h3>同じ役ならキッカーまで比べる</h3><p>たとえば同じAのワンペアでも、残りのカードがKの人は9の人に勝ちます。5枚すべて同じならポットを分けます。</p></div></div>
              </div>
              <div className="kicker-example">
                <div className="kicker-example__head"><span>KICKER EXAMPLE</span><h4>② ワンペア同士の比較例</h4></div>
                <CardRow label="ボード"><PokerCard rank="A" suit="c" compact /><PokerCard rank="8" suit="d" compact /><PokerCard rank="6" suit="s" compact /><PokerCard rank="3" suit="h" compact /><PokerCard rank="2" suit="c" compact /></CardRow>
                <div className="kicker-example__players">
                  <div className="kicker-example__player"><div className="kicker-example__player-top"><b>AK</b><span className="kicker-badge kicker-badge--win">WIN</span></div><CardRow label="手札"><PokerCard rank="A" suit="s" compact /><PokerCard rank="K" suit="d" compact /></CardRow><p><strong>Aのワンペア（AAK86）</strong><br />A9と役は同じでも、3枚目のKが9より強いので勝ちます。</p></div>
                  <div className="kicker-example__player"><div className="kicker-example__player-top"><b>A9</b><span className="kicker-badge kicker-badge--lose">LOSE</span></div><CardRow label="手札"><PokerCard rank="A" suit="h" compact /><PokerCard rank="9" suit="s" compact /></CardRow><p><strong>Aのワンペア（AA986）</strong><br />役は同じでも、3枚目の強さで負けます。</p></div>
                </div>
              </div>
              <div className="board-play-example"><div><span>BOARD PLAYS</span><b>ボードだけで役が完成する例</b></div><CardRow><PokerCard rank="A" suit="s" compact /><PokerCard rank="K" suit="h" compact /><PokerCard rank="Q" suit="d" compact /><PokerCard rank="J" suit="c" compact /><PokerCard rank="10" suit="s" compact /></CardRow><p>このボードはAハイストレート。誰の手札でもこれを上回れなければ、残ったプレイヤー同士で同じ役になります。</p></div>
            </section>

            <section id="nolimit" className="section-block">
              <div className="section-head"><span>07</span><div><p>NO LIMIT</p><h2>レイズ額について</h2></div></div>
              <p className="section-intro">自分の番なら、ルール上必要な最低額以上であれば、<strong>自分の持ちチップすべてまで</strong>ベット・レイズできます。ここでは、特に初心者が迷いやすい<strong>最低レイズ額</strong>の考え方を確認します。</p>
              <div className="nolimit-grid"><div className="nolimit-card"><span>BET</span><h3>ベット</h3><p>誰もまだベットしていない場面で賭けること。通常は最低でもBB額以上。</p></div><div className="nolimit-card"><span>RAISE</span><h3>最低レイズの考え方</h3><p>基本は「直前のベットまたはレイズで増えた額」以上をさらに上乗せします。</p></div><div className="nolimit-card is-dark"><span>ALL-IN</span><h3>最大は自分の残り全部</h3><p>スタックが3,400なら、最大3,400まで。相手より少なくてもオールインできます。</p></div></div>
              <div className="raise-example"><div><small>例：BB 200</small><strong>200</strong><span>→</span><small>Aさんが500へレイズ</small><strong>500</strong><span>→</span><small>次の最低リレイズ</small><strong>800</strong></div><p>200→500で「300増えた」ため、次にレイズするなら少なくともさらに300上げて800以上。</p></div>
              <div className="minraise-note"><strong>つまり、最初にレイズする際は2倍が最小レイズ額</strong>となります。たとえばBBが200なら、最初のレイズは少なくとも400です。</div>
              <div className="minraise-point"><span>POINT</span><p>2倍出せば最小レイズ条件は満たせます。</p></div>
              <div className="sidepot-note"><b>サイドポット</b><p>複数人で1人だけ少ないチップをオールインし、他のプレイヤーがさらに賭け続けると追加分は別の「サイドポット」になります。オールインした人が勝てるのは、自分が参加した額までのポットです。</p></div>
            </section>

            <section id="starting" className="section-block">
              <div className="section-head"><span>08</span><div><p>STARTING HANDS</p><h2>最初の手札は、この3タイプだけ知ればOK</h2></div></div>
              <p className="section-intro">ここは戦略というより用語の確認です。最初から細かいレンジを覚える必要はありません。</p>
              <div className="hand-examples">
                <div className="hand-example"><div className="hand-example__top"><span>HIGH POCKET PAIR</span><b>高いポケットペア</b></div><CardRow><PokerCard rank="A" suit="s" /><PokerCard rank="A" suit="h" /></CardRow><p><strong>AA・KK・QQ</strong>など。同じ数字2枚の組み合わせで、プリフロップでは非常に強い部類。</p></div>
                <div className="hand-example"><div className="hand-example__top"><span>HIGH CARDS</span><b>高いカードの組み合わせ</b></div><CardRow><PokerCard rank="A" suit="s" /><PokerCard rank="K" suit="d" /></CardRow><p><strong>AK・AQ・KQ</strong>など。高いワンペアやストレートを作りやすい代表的な形。</p></div>
                <div className="hand-example"><div className="hand-example__top"><span>SUITED CONNECTOR</span><b>スーテッドコネクター</b></div><CardRow><PokerCard rank="J" suit="s" /><PokerCard rank="10" suit="s" /></CardRow><p><strong>J♠10♠・10♥9♥</strong>など。同じスートで数字が連続した2枚。フラッシュとストレートの両方を狙える形。</p></div>
              </div>
              <p className="small-note">「s」は suited（同じスート）、「o」は offsuit（別スート）の略。例：AKs / AKo。</p>
            </section>

            <section className="article-summary">
              <p className="eyebrow">QUICK RECAP</p>
              <h2>最初の1ゲーム前に、ここだけ再確認</h2>
              <div className="summary-line"><span>1</span><b>6人戦では、プリフロップはUTGから、フロップ以降はSBからアクション。</b></div>
              <div className="summary-line"><span>2</span><b>手札2枚＋ボード最大5枚の中から、一番強い5枚を使う。</b></div>
              <div className="summary-line"><span>3</span><b>未ベットならチェック/ベット、ベット済みならコール/レイズを選ぶ。</b></div>
              <div className="summary-line"><span>4</span><b>フロップ → ターン → リバーの各段階でベッティング。</b></div>
              <div className="summary-line"><span>5</span><b>全員を降ろすか、ショーダウンで最も強い5枚を作れば勝ち。</b></div>
            </section>
          </article>
        </div>

        <section className="article-cta">
          <div className="article-cta__copy">
            <p>AKIHABARA / KANDA · 24H</p>
            <h2>NLHも、ポーカーレンタルスペース デュエルの貸卓で。</h2>
            <span>秋葉原・神田で、ポーカーテーブル・チップ・トランプ・自動シャッフル機を完備。テキサスホールデムの練習や友人同士のポーカー会にも、手ぶらでご利用いただけます。</span>
          </div>
          <div className="article-cta__actions">
            <a className="article-cta__reserve" href="https://upnow.jp/duel">今すぐ予約する <small>登録不要</small></a>
            <a className="article-cta__internal" href="/#pricing">料金・設備を確認する →</a>
          </div>
        </section>
      </main>
    </div>
  );
}
